import { SOLUTION_TYPE, SolutionType } from '../../../constants/user-info/solutionType';
import { userRoles, UserRoleTypes } from '../../../constants/userRoles';
import { pluralizeString } from '../../stringUtils/pluralizeString/pluralizeString';
import { CaseType, textTransform } from '../../stringUtils/textTransform/textTransform';

type UserInfo = {
  solution?: SolutionType | SolutionType[] | null;
};

export type FormatConfig = {
  case?: CaseType;
  pluralize?: boolean;
};

// role should be in capital case and without pluralization by default
const BASE_ROLE_MAPPINGS = {
  [userRoles.SUPERUSER]: 'Super Admin',
  [userRoles.ADMIN]: 'Entity Admin',
  [userRoles.AGENT]: 'Coach',
  [userRoles.CUSTOMER]: 'User',
  [userRoles.FOOD_ADMIN]: 'Food Admin',
  [userRoles.INBODY_ADMIN]: 'InBody Admin',
} as const;

const SCAN_IQ_ROLE_MAPPINGS = {
  [userRoles.SUPERUSER]: 'Super Admin',
  [userRoles.ADMIN]: 'Entity Admin',
  [userRoles.AGENT]: 'Staff Member',
  [userRoles.CUSTOMER]: 'User',
  [userRoles.FOOD_ADMIN]: 'Food Admin',
  [userRoles.INBODY_ADMIN]: 'InBody Admin',
} as const;

const CHALLENGE_ROLE_MAPPINGS = {
  [userRoles.SUPERUSER]: 'Super Admin',
  [userRoles.ADMIN]: 'Entity Admin',
  [userRoles.AGENT]: 'Staff Member',
  [userRoles.CUSTOMER]: 'User',
  [userRoles.FOOD_ADMIN]: 'Food Admin',
  [userRoles.INBODY_ADMIN]: 'InBody Admin',
} as const;

const getRoleMapping = (
  userRole: UserRoleTypes,
  solution?: SolutionType | null,
): string => {
  switch (solution) {
    case SOLUTION_TYPE.SCAN_IQ:
      return SCAN_IQ_ROLE_MAPPINGS[userRole];
    case SOLUTION_TYPE.CHALLENGE:
      return CHALLENGE_ROLE_MAPPINGS[userRole];
    default:
      return BASE_ROLE_MAPPINGS[userRole];
  }
};

// explicit display order for multi-solution role labels (e.g. "Coach/Staff Member")
const SOLUTION_LABEL_ORDER: SolutionType[] = [
  SOLUTION_TYPE.NUTRITION,
  SOLUTION_TYPE.SCAN_IQ,
  SOLUTION_TYPE.CHALLENGE,
];

const sortByLabelOrder = (solutions: SolutionType[]): SolutionType[] =>
  SOLUTION_LABEL_ORDER.filter((solution) => solutions.includes(solution));

export const getHumanizeUserRole = (
  userRole: UserRoleTypes,
  userInfo?: UserInfo,
  /**
   * @description Format config for the role
   * @default  case: 'capital', pluralize: false
   */
  formatConfig?: FormatConfig,
): string => {
  const { solution } = userInfo ?? {};
  // empty array → no-solution context (fallback to base mapping)
  const solutions = Array.isArray(solution)
    ? solution.length === 0
      ? [null]
      : sortByLabelOrder(solution)
    : [solution];

  const labels = [...new Set(solutions.map((s) => getRoleMapping(userRole, s)))];

  let role = formatConfig?.pluralize
    ? labels.map(pluralizeString).join('/')
    : labels.join('/');

  if (formatConfig?.case) {
    role = textTransform(role, formatConfig.case);
  }

  return role;
};
